/* eslint-disable import/no-extraneous-dependencies */
import { ReactSelect, Select } from 'crux';
import React, { useEffect, useRef } from 'react';
import get from 'lodash/get';
import isPlainObject from 'lodash/isPlainObject';
import { at } from 'lodash';
import { connect } from 'react-redux';
import { withTheme } from 'styled-components';
import { axiosInstance } from '../util/axios';
import { updateAnswersArray } from '../redux/actions';
import {
  getLabelProps,
  getWidth,
  getTooltipProps,
  getCSSVariables,
  getTimeStampData,
  getGroupQAriaLabel,
} from './utils';
import { SelectWrapper } from './utils/styles';
import { scrollQuestionIntoView, parseHtml, getUTCTimestamp, isQuotePage } from '../utils';
import { updateQuestionAnswerTimeStamp } from '../redux/actions/questions';

const getDefaultOption = (question) => {
  const { response, response_options: responseOptions = [] } = question || {};
  if (isPlainObject(response)) {
    return response?.label === '' ? '' : response?.id;
  }
  const selectedOption = responseOptions.find((option) => option?.id === response);
  return !selectedOption?.label ? '' : response;
};

const SelectOption = ({
  question,
  uid,
  theme,
  handleSelectOption,
  isEditMode,
  type,
  screenViewport,
  lastFocusedElementId,
  role,
  updateTimeStamp,
  meta,
  questionsArray,
  // updateAnswerArrayInSocket,
}) => {
  // Setting the initial response for the field

  const cssVariables = getCSSVariables(theme);
  const font = at(cssVariables, ['pageOverride.font', 'global.font']);
  const {
    pageId,
    config: {
      theme: {
        pageOverrides,
        components,
        global: { colorScheme, colorOverride, reactSelect, errorMessageColor },
      },
    },
  } = theme;

  const {
    question_id: questionId,
    question_text: questionText,
    question_type: questionType,
    display_type: displayType,
    response_options: responseOptions,
    hint_title: tooltipTitle,
    hint_text: tooltipBody,
    hint_html: tooltipHTML,
    show_as_required: showAsRequired,
    // isControlledInput,
    tooltip,
    validations,
    is_editable: isEditable,
    helper_text: helperText,
    transaction_log: transactionLog,
    last_submitted_at: lastSubmittedAt,
  } = question;

  useEffect(() => {
    if (question.response || isEditMode) {
      handleSelectOption(question.response, true);
      updateTimeStamp({ transactionLog, lastSubmittedAt }, true);
    }
  }, []);

  // Tab Navigation
  const inputRef = useRef();
  useEffect(() => {
    const shouldFocus = lastFocusedElementId === questionId;
    const focusDiv = document.getElementById(lastFocusedElementId);
    if (inputRef && inputRef.current && shouldFocus && meta.os !== 'MacOS') {
      if (typeof inputRef.current.focus === 'function') {
        inputRef.current.focus();
        setTimeout(() => {
          document.activeElement.blur();
        }, 10);
      }
    } else if (meta.os === 'MacOS' && focusDiv && typeof focusDiv.focus === 'function') {
      focusDiv.focus();
    }
  }, []);

  const defaultSelected = getDefaultOption(question);

  const {
    floatingLabel,
    labelPosition,
    labelIsHTML,
    disableCapitalization = false,
    tooltipPopperOffsetLeftPos,
  } = getLabelProps('selectOption', components, pageId, pageOverrides);

  const questionLayout = get(question, 'properties.layout.value');
  const showOptionalText = get(theme, 'config.theme.global.showOptionalText', false);
  const screenReaderLabelQuestionId = get(question, 'screen_reader_label_id', null);
  const ariaLabel = getGroupQAriaLabel(screenReaderLabelQuestionId, questionsArray, pageId);

  let questionLabelPosition = questionLayout === 'two' ? 'left' : labelPosition;
  const timeStampRef = useRef(null);

  // extract labelPosition key from properties in response as per deviceType
  // eg: labelPosition[mobile], labelPosition[tablet] or labelPosition[desktop]
  const _questionLabelPosition = get(
    question,
    `properties.labelPosition[${screenViewport.toLowerCase()}]`,
  );
  if (_questionLabelPosition) {
    questionLabelPosition = _questionLabelPosition;
  }

  const themeConfiguration = colorOverride
    ? {
        ...colorOverride,
      }
    : null;

  if (reactSelect) {
    reactSelect.selectLabel.font =
      font?.[0]?.['selectOption-label'] || font?.[1]?.['selectOption-label'];
    reactSelect.placeholder.font =
      font?.[0]?.['selectOption-placeholder'] || font?.[1]?.['selectOption-placeholder'];
  }

  const reactSlectThemeConfiguration = reactSelect
    ? {
        ...reactSelect,
      }
    : null;

  const disabledTheme = get(components, 'selectOption.disabledTheme', {});

  const getThemeConfiguration = () => {
    return {
      ...themeConfiguration,
      ...reactSlectThemeConfiguration,
      ...(!isEditable && { ...disabledTheme }),
    };
  };
  const tooltipColor = get(theme, 'config.theme.global.toolTip.color', '');
  const tooltipFont = get(theme, 'config.theme.global.toolTip.font', '');

  // AutoComplete-Text Theme Configs
  const autoCompleteTextTheme = get(components, 'autocomplete-text.themeConfiguration', {});
  autoCompleteTextTheme.font = font?.[0]?.['input-label'] || font?.[1]?.['input-label'];
  autoCompleteTextTheme.placeholderFont =
    font?.[0]?.['input-placeholder'] || font?.[1]?.['input-placeholder'];

  // const getValue = (selectedValue, options) => {
  // 	if(type === 'M' && selectedValue) {
  // 		return options.filter(option => selectedValue.includes(option.id) || selectedValue.includes(option.label));
  // 	}
  // 	const valueObject = options.filter(option=>(option.id===selectedValue || option.label===selectedValue))
  // 	return valueObject[0]
  // }
  const size = get(question, 'properties.size.value', '');
  const tooltipProps = getTooltipProps(tooltipTitle, tooltipBody, tooltipHTML || tooltip);

  const customSelectPlaceholder = validations?.placeholder_text?.hide
    ? ''
    : get(validations, 'custom_placeholder.value', 'Select...');
  const isAsteriskConfig = get(theme, 'config.theme.components.inputAsteriskRequired');
  let isAsterisk;
  if (
    isAsteriskConfig &&
    question.validations.required &&
    question.validations.required.value === true
  ) {
    isAsterisk = true;
  }

  const handleFocus = (e) => {
    const element = e.target;
    scrollQuestionIntoView({
      element,
      theme,
      role,
      screenViewport,
    });
    timeStampRef.current.timeStart = getUTCTimestamp();
  };

  const handleBlur = () => {
    updateTimeStamp(getTimeStampData(timeStampRef, transactionLog, meta));
    // updateAnswerArrayInSocket();
  };

  const customFilterFn = (option, searchText) => {
    const modifiedLabel = option.label
      .replace(/[,!@#$%^&*)(+=._-]/g, '')
      .toLowerCase()
      .trim();
    const modifiedSearchText = searchText
      .replace(/[,!@#$%^&*)(+=._-]/g, '')
      .toLowerCase()
      .trim();
    return modifiedLabel.startsWith(modifiedSearchText);
  };

  const parseResponseOptions = (options) => {
    return options.map((o) => {
      return {
        ...o,
        label: parseHtml(o.label).toString(),
      };
    });
  };

  if (
    displayType === 'dropdown' ||
    displayType === 'radio_button' ||
    displayType === 'dropdown_search'
  ) {
    return (
      <SelectWrapper
        ref={ timeStampRef }
        floatingLabel={ floatingLabel }
        disabled={ !isEditable }
        onBlur={ handleBlur }
      >
        <ReactSelect
          // isControlled={ isControlledInput }
          // value={ getValue(defaultSelected, responseOptions) }
          key={ questionId }
          id={ questionId }
          inputRef={ inputRef }
          floatingLabel={ floatingLabel }
          labelPosition={ questionLabelPosition }
          ariaLabel={ ariaLabel }
          labelText={ questionText }
          labelIsHTML={ labelIsHTML }
          placeholder='Please select an option'
          customSelectPlaceholder={ customSelectPlaceholder }
          options={ parseResponseOptions(responseOptions) }
          color={ colorScheme }
          themeConfiguration={ getThemeConfiguration() }
          defaultSelected={ defaultSelected }
          handleSelect={ (selected) => {
            if ((isPlainObject(selected) ? selected?.id : selected) !== question.response) {
              handleSelectOption(selected, false, true);
            }
          } }
          onFocus={ handleFocus }
          onTooltipFocus={ handleFocus }
          type={ type }
          width={ getWidth('selectOption', pageId, pageOverrides, size, screenViewport) }
          isFilterable
          tooltipPlacement='label'
          tooltipColor={ tooltipColor }
          tooltipFont={ tooltipFont }
          { ...tooltipProps }
          disableCapitalization={ disableCapitalization }
          isAsterisk={ isAsterisk }
          disabled={ !isEditable }
          errorMessageColor={ errorMessageColor }
          helperText={ helperText }
          wcagTooltipWrapper={ tooltipProps.hasTooltip }
          tooltipAriaLabel={ `${questionText} info dialog` }
          removeAriaLabelforPopper
          showOptionalText={ showOptionalText && !isQuotePage() }
          isWcagOptional={ !(showAsRequired || question?.validations?.required?.value) }
          tooltipPopperOffsetLeftPos={ tooltipPopperOffsetLeftPos }
          optionClassName='app-select-option'
          selectAriaLabel=''
          isCustomInput
          customFilterFn={ customFilterFn }
        />
      </SelectWrapper>
    );
  }

  if (questionType === 'autocomplete-text') {
    const defaultParams = {
      uid,
      question_id: questionId,
      properties: JSON.stringify(get(question, 'properties.autocomplete', {})),
    };
    const allowUnrecognized = get(question, 'properties.autocomplete.allow_unrecognized', false);
    return (
      <SelectWrapper>
        <Select
          id={ questionId }
          floatingLabel={ false }
          inputRef={ inputRef }
          labelPosition='top'
          labelText={ questionText }
          labelIsHTML
          placeholder='Enter 3 or more characters'
          customSelectPlaceholder={ customSelectPlaceholder }
          asyncProps={ {
            api: '/search',
            method: 'post',
            defaultParams,
            searchKey: 'key',
            fetchFn: axiosInstance,
            responseMap: {
              id: 'id',
              label: 'value',
            },
            allowUnrecognized,
          } }
          color={ colorScheme }
          themeConfiguration={ { ...autoCompleteTextTheme } }
          handleSelect={ (selected, makeApiCall) => {
            handleSelectOption(selected, false, true, makeApiCall);
          } }
          defaultSelected={ defaultSelected }
          type='A'
          { ...tooltipProps }
          isAsterisk={ isAsterisk }
        />
      </SelectWrapper>
    );
  }
  return null;
};

const mapStateToProps = (state, props) => {
  const {
    uid,
    meta: { screenViewport },
    questions: { lastFocusedElementId, role, activeSubsectionId, sections },
  } = state;
  const { sectionIndex } = props;
  const questionsArray = get(sections[activeSubsectionId], `[${sectionIndex}].questionList`, []);
  return {
    uid,
    screenViewport,
    lastFocusedElementId,
    role,
    meta: state.meta,
    questionsArray,
  };
};

const mapDispatchToProps = (dispatch, props) => {
  const transactionLog = get(props, 'question.transaction_log', []);
  const questionId = get(props, 'question.question_id', '');
  return {
    handleSelectOption: (response, preventReflexiveCall, hasEdit, makeApiCall) => {
      const auditRequired = get(props, 'question.audit_required', false);
      const responseOptions = get(props, 'question.response_options', []);
      const doesResponseHasFormat = responseOptions.some(
        (option) => get(option, 'format[0].target') && get(option, 'format[0].value'),
      );

      if (doesResponseHasFormat) {
        // get formatArr by response
        const [inputRes1, inputRes2] = response.format;
        dispatch(
          updateAnswersArray({
            questionId: inputRes1.target,
            response: inputRes1.value,
            sectionIndex: props.sectionIndex,
            preventReflexiveCall: true,
            propQuestion: inputRes1.target,
            listParentId: props.listParentId,
            listIndex: props.listIndex,
            transactionLog,
          }),
        );
        dispatch(
          updateAnswersArray({
            questionId: inputRes2.target,
            response: inputRes2.value,
            sectionIndex: props.sectionIndex,
            preventReflexiveCall: true,
            propQuestion: inputRes2.target,
            listParentId: props.listParentId,
            listIndex: props.listIndex,
          }),
        );
      }

      const { type } = props;
      // for multi-select and auto-suggest, store array.
      let updatedResponse = [response];
      if (type === 'A') {
        if (!isPlainObject(response)) {
          updatedResponse = response;
        }
      } else if (type === 'M') {
        if (response && !isPlainObject(response)) {
          updatedResponse = response.map((option) => option.id);
        }
      } else {
        updatedResponse = isPlainObject(response) ? response.id : response;
      }
      const triggerValidation = get(props, 'question.validations.trigger_validation.value');
      const isError = isPlainObject(response) && !!response.error_message;
      dispatch(
        updateAnswersArray({
          questionId,
          response: updatedResponse,
          triggerValidation,
          hasEdit,
          questionResponse: updatedResponse,
          sectionIndex: props.sectionIndex,
          preventReflexiveCall,
          propQuestion: props.question,
          listParentId: props.listParentId,
          listIndex: props.listIndex,
          auditRequired,
          makeApiCall,
          isNestedQ: props.isNestedQ,
          parentListIndex: props.parentListIndex,
          isError,
        }),
      );
    },
    updateTimeStamp: (timeStampData, isPrefilled = false) => {
      dispatch(
        updateQuestionAnswerTimeStamp(questionId, timeStampData, props.sectionIndex, isPrefilled),
      );
    },
    // updateAnswerArrayInSocket: () => {
    //   const childQuestions = get(props, 'question.child_questions', false);
    //   const reflexiveQuestionFlag = get(props, 'question.reflexive_question_flag', false);
    //   dispatch(updateQuestionAnswerInSocket(questionId, childQuestions || reflexiveQuestionFlag));
    // },
  };
};

export default withTheme(connect(mapStateToProps, mapDispatchToProps)(SelectOption));
